/**
 * QA State Machine - Manages the sequential Q&A practice session flow
 *
 * States:
 * IDLE → COUNTDOWN → MURPHY_ASKING → AWAITING_ANSWER → PROCESSING → (loop) → COMPLETE
 *
 * This state machine orchestrates the Q&A practice flow where Murphy asks
 * questions sequentially, waits for user responses via VAD, and auto-advances.
 */

export const QA_STATES = {
    IDLE: 'idle',
    COUNTDOWN: 'countdown',
    MURPHY_ASKING: 'murphy_asking',
    AWAITING_ANSWER: 'awaiting_answer',
    PROCESSING: 'processing',
    COMPLETE: 'complete',
    ERROR: 'error'
};

export const QA_EVENTS = {
    START_SESSION: 'start_session',
    COUNTDOWN_COMPLETE: 'countdown_complete',
    TTS_STARTED: 'tts_started',
    TTS_ENDED: 'tts_ended',
    SPEECH_DETECTED: 'speech_detected',
    SILENCE_DETECTED: 'silence_detected',
    TIMEOUT: 'timeout',
    NEXT_QUESTION: 'next_question',
    SESSION_COMPLETE: 'session_complete',
    ERROR: 'error',
    RESET: 'reset'
};

/**
 * Creates initial state for a Q&A session
 * @param {Array} questions - Array of question objects from backend
 * @returns {Object} Initial state
 */
export const createInitialState = (questions = []) => ({
    currentState: QA_STATES.IDLE,
    currentQuestionIndex: 0,
    totalQuestions: questions.length,
    questions: questions,
    answers: [],
    sessionStartTime: null,
    sessionEndTime: null,
    currentQuestionStartTime: null,
    error: null
});

/**
 * State transition logic
 * @param {Object} state - Current state
 * @param {string} event - Event from QA_EVENTS
 * @param {Object} payload - Optional event payload
 * @returns {Object} New state
 */
export const qaReducer = (state, event, payload = {}) => {
    const { currentState, currentQuestionIndex, totalQuestions, questions, answers } = state;

    switch (event) {
        case QA_EVENTS.START_SESSION:
            if (currentState !== QA_STATES.IDLE) {
                console.warn('QA State Machine: Cannot start session, not in IDLE state');
                return state;
            }
            if (totalQuestions === 0) {
                return {
                    ...state,
                    currentState: QA_STATES.ERROR,
                    error: 'No questions available for this session'
                };
            }
            return {
                ...state,
                currentState: QA_STATES.COUNTDOWN,
                sessionStartTime: Date.now()
            };

        case QA_EVENTS.COUNTDOWN_COMPLETE:
            if (currentState !== QA_STATES.COUNTDOWN) {
                return state;
            }
            return {
                ...state,
                currentState: QA_STATES.MURPHY_ASKING,
                currentQuestionStartTime: Date.now()
            };

        case QA_EVENTS.TTS_STARTED:
            // Murphy has started speaking - ensure we're in the right state
            if (currentState === QA_STATES.MURPHY_ASKING) {
                return state; // Already in correct state
            }
            return state;

        case QA_EVENTS.TTS_ENDED:
            if (currentState !== QA_STATES.MURPHY_ASKING) {
                return state;
            }
            return {
                ...state,
                currentState: QA_STATES.AWAITING_ANSWER
            };

        case QA_EVENTS.SPEECH_DETECTED:
            // User started speaking - we can track this but state doesn't change
            return state;

        case QA_EVENTS.SILENCE_DETECTED:
            if (currentState !== QA_STATES.AWAITING_ANSWER) {
                return state;
            }
            // Record the answer and move to processing
            const newAnswer = {
                questionIndex: currentQuestionIndex,
                question: questions[currentQuestionIndex],
                skipped: false,
                startTime: state.currentQuestionStartTime,
                endTime: Date.now(),
                speechDuration: payload.speechDuration || 0,
                silenceDuration: payload.silenceDuration || 0
            };
            return {
                ...state,
                currentState: QA_STATES.PROCESSING,
                answers: [...answers, newAnswer]
            };

        case QA_EVENTS.TIMEOUT:
            if (currentState !== QA_STATES.AWAITING_ANSWER) {
                return state;
            }
            // User timed out - mark as skipped
            const skippedAnswer = {
                questionIndex: currentQuestionIndex,
                question: questions[currentQuestionIndex],
                skipped: true,
                skipReason: payload.reason || 'timeout',
                startTime: state.currentQuestionStartTime,
                endTime: Date.now()
            };
            return {
                ...state,
                currentState: QA_STATES.PROCESSING,
                answers: [...answers, skippedAnswer]
            };

        case QA_EVENTS.NEXT_QUESTION:
            if (currentState !== QA_STATES.PROCESSING) {
                return state;
            }
            const nextIndex = currentQuestionIndex + 1;
            if (nextIndex >= totalQuestions) {
                // No more questions - complete the session
                return {
                    ...state,
                    currentState: QA_STATES.COMPLETE,
                    sessionEndTime: Date.now()
                };
            }
            // Move to next question
            return {
                ...state,
                currentState: QA_STATES.MURPHY_ASKING,
                currentQuestionIndex: nextIndex,
                currentQuestionStartTime: Date.now()
            };

        case QA_EVENTS.SESSION_COMPLETE:
            return {
                ...state,
                currentState: QA_STATES.COMPLETE,
                sessionEndTime: Date.now()
            };

        case QA_EVENTS.ERROR:
            return {
                ...state,
                currentState: QA_STATES.ERROR,
                error: payload.error || 'An unknown error occurred'
            };

        case QA_EVENTS.RESET:
            return createInitialState(questions);

        default:
            console.warn('QA State Machine: Unknown event', event);
            return state;
    }
};

/**
 * QA Session Manager Class
 * Wraps the state machine with a convenient API and event callbacks
 */
export class QASessionManager {
    /**
     * @param {Array} questions - Array of question objects from backend
     * @param {Object} callbacks - Event callbacks
     */
    constructor(questions = [], callbacks = {}) {
        this.state = createInitialState(questions);
        this.callbacks = {
            onStateChange: null,
            onQuestionStart: null,
            onAwaitingAnswer: null,
            onAnswerRecorded: null,
            onQuestionSkipped: null,
            onSessionComplete: null,
            onError: null,
            ...callbacks
        };
        this.countdownTimeoutId = null;
        this.processingTimeoutId = null;
        this.awaitingAnswerTimeoutId = null;
    }

    /**
     * Get current state
     */
    getState() {
        return this.state;
    }

    /**
     * Get current question
     */
    getCurrentQuestion() {
        return this.state.questions[this.state.currentQuestionIndex] || null;
    }

    /**
     * Get progress info
     */
    getProgress() {
        return {
            currentQuestion: this.state.currentQuestionIndex + 1,
            totalQuestions: this.state.totalQuestions,
            answeredCount: this.state.answers.length,
            skippedCount: this.state.answers.filter(a => a.skipped).length
        };
    }

    /**
     * Dispatch an event to the state machine
     * @param {string} event - Event from QA_EVENTS
     * @param {Object} payload - Optional payload
     */
    dispatch(event, payload = {}) {
        const oldState = this.state.currentState;
        this.state = qaReducer(this.state, event, payload);
        const newState = this.state.currentState;

        // Only fire callbacks when state actually changes.
        // Events like SPEECH_DETECTED don't change state, so re-firing
        // callbacks (e.g. onAwaitingAnswer → startVAD) caused duplicates.
        if (oldState !== newState) {
            if (this.callbacks.onStateChange) {
                this.callbacks.onStateChange(newState, oldState, this.state);
            }
            this.handleStateCallbacks(newState, oldState, payload);
        }
    }

    /**
     * Handle state-specific callbacks
     */
    handleStateCallbacks(newState, oldState, payload) {
        switch (newState) {
            case QA_STATES.MURPHY_ASKING:
                if (oldState !== QA_STATES.MURPHY_ASKING && this.callbacks.onQuestionStart) {
                    this.callbacks.onQuestionStart(this.getCurrentQuestion(), this.getProgress());
                }
                break;

            case QA_STATES.AWAITING_ANSWER:
                if (this.callbacks.onAwaitingAnswer) {
                    this.callbacks.onAwaitingAnswer(this.getCurrentQuestion(), this.getProgress());
                }
                // Manual advance only (2026-09-02): no auto-skip timeout. The rep
                // advances with the "Next" button when they finish, so a long
                // answer is never cut off. The recording captures the whole time.
                break;

            case QA_STATES.PROCESSING:
                const latestAnswer = this.state.answers[this.state.answers.length - 1];
                if (latestAnswer) {
                    if (latestAnswer.skipped && this.callbacks.onQuestionSkipped) {
                        this.callbacks.onQuestionSkipped(latestAnswer, this.getProgress());
                    } else if (!latestAnswer.skipped && this.callbacks.onAnswerRecorded) {
                        this.callbacks.onAnswerRecorded(latestAnswer, this.getProgress());
                    }
                }
                break;

            case QA_STATES.COMPLETE:
                if (this.callbacks.onSessionComplete) {
                    this.callbacks.onSessionComplete(this.getSessionSummary());
                }
                break;

            case QA_STATES.ERROR:
                if (this.callbacks.onError) {
                    this.callbacks.onError(this.state.error);
                }
                break;
        }
    }

    /**
     * Start the Q&A session with a countdown
     * @param {number} countdownSeconds - Seconds to countdown before first question (default: 3)
     */
    startSession(countdownSeconds = 3) {
        this.dispatch(QA_EVENTS.START_SESSION);

        if (this.state.currentState === QA_STATES.COUNTDOWN) {
            // Start countdown
            this.countdownTimeoutId = setTimeout(() => {
                this.dispatch(QA_EVENTS.COUNTDOWN_COMPLETE);
            }, countdownSeconds * 1000);
        }
    }

    /**
     * Notify that TTS has started playing
     */
    notifyTTSStarted() {
        this.dispatch(QA_EVENTS.TTS_STARTED);
    }

    /**
     * Notify that TTS has finished playing
     */
    notifyTTSEnded() {
        this.dispatch(QA_EVENTS.TTS_ENDED);
    }

    /**
     * Notify that speech was detected
     */
    notifySpeechDetected() {
        // Manual advance only: nothing here may arm a timer. This used to
        // re-arm the retired 90s auto-skip, unreachable only because no caller
        // starts the detector; re-enabling it for a speaking indicator would
        // have brought the answer-truncation bug straight back (review,
        // 2026-09-04).
        this.dispatch(QA_EVENTS.SPEECH_DETECTED);
    }

    /**
     * Notify that silence was detected (user finished answering)
     * @param {Object} data - Speech/silence duration data
     */
    notifySilenceDetected(data = {}) {
        this.clearAwaitingAnswerTimeout();
        this.dispatch(QA_EVENTS.SILENCE_DETECTED, data);

        // Auto-advance to next question after a brief pause
        this.scheduleNextQuestion();
    }

    /**
     * Notify that the user timed out without speaking
     * @param {Object} data - Timeout reason data
     */
    notifyTimeout(data = {}) {
        this.clearAwaitingAnswerTimeout();
        this.dispatch(QA_EVENTS.TIMEOUT, data);

        // Auto-advance to next question after a brief pause
        this.scheduleNextQuestion();
    }

    // The 90s AWAITING_ANSWER safety timeout is gone entirely (manual advance
    // only). clearAwaitingAnswerTimeout stays as a harmless no-op guard.

    clearAwaitingAnswerTimeout() {
        if (this.awaitingAnswerTimeoutId) {
            clearTimeout(this.awaitingAnswerTimeoutId);
            this.awaitingAnswerTimeoutId = null;
        }
    }

    /**
     * Schedule advancement to next question
     * @param {number} delayMs - Delay before next question (default: 1500)
     */
    scheduleNextQuestion(delayMs = 1500) {
        if (this.processingTimeoutId) {
            clearTimeout(this.processingTimeoutId);
        }

        this.processingTimeoutId = setTimeout(() => {
            this.dispatch(QA_EVENTS.NEXT_QUESTION);
        }, delayMs);
    }

    /**
     * Force complete the session
     */
    completeSession() {
        this.dispatch(QA_EVENTS.SESSION_COMPLETE);
    }

    /**
     * Report an error
     * @param {string} error - Error message
     */
    reportError(error) {
        this.dispatch(QA_EVENTS.ERROR, { error });
    }

    /**
     * Reset the session
     */
    reset() {
        this.clearTimers();
        this.dispatch(QA_EVENTS.RESET);
    }

    /**
     * Clear all internal timers
     */
    clearTimers() {
        if (this.countdownTimeoutId) {
            clearTimeout(this.countdownTimeoutId);
            this.countdownTimeoutId = null;
        }
        if (this.processingTimeoutId) {
            clearTimeout(this.processingTimeoutId);
            this.processingTimeoutId = null;
        }
        this.clearAwaitingAnswerTimeout();
    }

    /**
     * Cleanup - call when unmounting
     */
    cleanup() {
        this.clearTimers();
    }

    /**
     * Get session summary for scoring
     */
    getSessionSummary() {
        const { sessionStartTime, sessionEndTime, answers, questions } = this.state;

        return {
            sessionId: this.sessionId,
            totalQuestions: questions.length,
            answeredQuestions: answers.filter(a => !a.skipped).length,
            skippedQuestions: answers.filter(a => a.skipped).length,
            sessionDuration: sessionEndTime ? sessionEndTime - sessionStartTime : null,
            answers: answers,
            questions: questions
        };
    }

    /**
     * Set the session ID (from backend)
     * @param {string} sessionId
     */
    setSessionId(sessionId) {
        this.sessionId = sessionId;
    }
}

export default QASessionManager;
